import ExcelJS from 'exceljs'
import { AuthedAxiosRequests } from 'features/auth/geckoAuthContext'
import { extractColumnExportString } from 'features/drilldown/components/table/columnRenders/explorerTableCellValues'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import {
  OntologyContextType,
  OntologyRolodexEntryType,
} from 'features/dynamicOntology'
import { createClassObjForEntryJson } from 'features/rolodex/rolodexEntryDispenser'
import chunk from 'lodash/chunk'
import flatMap from 'lodash/flatMap'
import {
  CacheableRolodexEntryJson,
  GeckoEntry,
  RolodexEntryType,
  RolodexLoadedEntryJson,
} from 'types'
import { downloadBlob } from 'utils/download'

export type ExplorerTableExportFormat = 'xlsx' | 'csv'

/**
 * How many group IDs to load per `/entries/load` request. Conservative so
 * the request body stays well under any practical size limit even with
 * upward links - 500 base entries usually expand to a few thousand
 * documents in the response.
 */
const DEFAULT_BATCH_SIZE = 500

/**
 * Hard cap on rows in a single export to keep the workbook from blowing
 * up memory or the worksheet limit. Mirrors the histogram cap used by
 * `usePaginatedQuery` so users never see a higher number on screen than
 * we can ship.
 */
const DEFAULT_MAX_ROWS = 60_000

const ENTRIES_LOAD_PATH = '/api/v2/entries/load'
const JSON_CONTENT_HEADERS = { 'Content-Type': 'application/json' } as const
const CHILD_LOAD_LIMIT = 10_000

const XLSX_WORKSHEET_NAME = 'Export'
const HEADER_ROW_INDEX = 1
const DEFAULT_COLUMN_WIDTH = 10
const MAX_COLUMN_WIDTH = 60
const COLUMN_WIDTH_PADDING = 2
const XLSX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const CSV_MIME_TYPE = 'text/csv;charset=utf-8;'
const CSV_DELIMITER = ','
const CSV_ROW_SEPARATOR = '\r\n'
const CSV_QUOTE = '"'
const CSV_QUOTE_ESCAPE = '""'
const CSV_ESCAPE_PATTERN = /[",\r\n]/
const CSV_QUOTE_PATTERN = /"/g

const EXPORT_CANCELLED_MESSAGE = 'Export cancelled'
const ABORT_ERROR_NAME = 'AbortError'
const EVENT_LOOP_YIELD_MS = 0

const FILE_EXTENSION_BY_FORMAT: Record<ExplorerTableExportFormat, string> = {
  xlsx: '.xlsx',
  csv: '.csv',
}

export type ExplorerTableExportColumn =
  | { kind: 'data'; title: string; columnDef: DrilldownTableColumnDef }
  | { kind: 'extra'; title: string; column: DrilldownTableExtraColumn }

export type ExplorerTableExportInput = {
  axios: AuthedAxiosRequests
  apiURL: string
  groupByType: OntologyRolodexEntryType
  /**
   * Upward links - same list the paginated UI uses to populate each row's
   * entry pool with the column's referenced types.
   */
  links: ReadonlyArray<RolodexEntryType>
  /**
   * Downward links - types whose entries should be loaded by tag for each
   * batch and merged in alongside the row entry, matching the paginated
   * UI's `linkedEntryQuery`.
   */
  childLinks: ReadonlyArray<OntologyRolodexEntryType | string>
  orderedGroupIds: ReadonlyArray<string>
  groupIdToBaseId: ReadonlyMap<string, ReadonlyArray<string>>
  expandRecords?: (
    rows: ReadonlyArray<PaginatedRolodexTableRecordType>
  ) => ReadonlyArray<PaginatedRolodexTableRecordType>
  columns: ReadonlyArray<ExplorerTableExportColumn>
  schemaFields: OntologyContextType['fields']
  format: ExplorerTableExportFormat
  filename: string
  /** Whether the underlying ID query was truncated by the histogram cap. */
  truncated: boolean
  batchSize?: number
  maxRows?: number
  onProgress?: (loaded: number, total: number) => void
  signal?: AbortSignal
}

export type ExplorerTableExportResult = {
  rowsExported: number
  totalRows: number
  truncatedByHistogram: boolean
  truncatedByMaxRows: boolean
}

async function postEntriesLoad(
  axios: AuthedAxiosRequests,
  apiURL: string,
  body: object,
  signal?: AbortSignal
): Promise<RolodexLoadedEntryJson[]> {
  const response = await axios.post(`${apiURL}${ENTRIES_LOAD_PATH}`, body, {
    headers: JSON_CONTENT_HEADERS,
    signal,
  })
  const data = response.data
  return Array.isArray(data) ? data : []
}

function flattenLoadedEntries(
  responses: ReadonlyArray<RolodexLoadedEntryJson>
): ReadonlyArray<GeckoEntry> {
  const pool: GeckoEntry[] = []
  const seen = new Set<string>()
  const push = (json: CacheableRolodexEntryJson | undefined) => {
    if (!json || seen.has(json.id)) return
    const obj = createClassObjForEntryJson(json)
    if (!obj) return
    seen.add(json.id)
    pool.push(obj)
  }
  responses.forEach(({ entry, links }) => {
    push(entry as CacheableRolodexEntryJson)
    if (links) {
      Object.values(links).forEach((linked) =>
        push(linked as CacheableRolodexEntryJson)
      )
    }
  })
  return pool
}

/**
 * Build the body for the primary `/entries/load` request - matches the UI
 * path in `useRolodexEntries`: query by IDs only, no `type`. The histogram
 * step has already pinned us to entries of `groupByType`, and Rolodex
 * rejects requests that specify the same type both via `type` and via
 * `links` (`Type(s) specified more than once`). Filter `groupByType` out
 * of `links` defensively in case a caller passes overlapping types.
 */
function createPrimaryLoadBody(
  baseIds: ReadonlyArray<string>,
  groupByType: OntologyRolodexEntryType,
  links: ReadonlyArray<RolodexEntryType>
): object {
  const primaryLinks = links.filter((t) => t !== groupByType)
  return {
    ids: [...baseIds],
    links: primaryLinks.length ? [...primaryLinks] : undefined,
    limit: baseIds.length,
  }
}

/**
 * Build the body for the downward-link `/entries/load` request, or return
 * `undefined` to skip it. Skipped when a child type collides with the row
 * type: the same Rolodex constraint as `createPrimaryLoadBody` applies and
 * there's nothing useful to load there anyway.
 */
function createChildLoadBody(
  baseIds: ReadonlyArray<string>,
  groupByType: OntologyRolodexEntryType,
  childLinks: ReadonlyArray<OntologyRolodexEntryType | string>
): object | undefined {
  const filteredChildLinks = childLinks.filter((t) => t !== groupByType)
  if (!filteredChildLinks.length) return undefined
  return {
    type: [...filteredChildLinks] as RolodexEntryType[],
    tags: { [groupByType]: [...baseIds] },
    limit: CHILD_LOAD_LIMIT,
  }
}

async function loadBatch({
  axios,
  apiURL,
  groupByType,
  baseIds,
  links,
  childLinks,
  signal,
}: {
  axios: AuthedAxiosRequests
  apiURL: string
  groupByType: OntologyRolodexEntryType
  baseIds: ReadonlyArray<string>
  links: ReadonlyArray<RolodexEntryType>
  childLinks: ReadonlyArray<OntologyRolodexEntryType | string>
  signal?: AbortSignal
}): Promise<ReadonlyArray<GeckoEntry>> {
  const primaryBody = createPrimaryLoadBody(baseIds, groupByType, links)
  const childBody = createChildLoadBody(baseIds, groupByType, childLinks)

  const [primary, child] = await Promise.all([
    postEntriesLoad(axios, apiURL, primaryBody, signal),
    childBody
      ? postEntriesLoad(axios, apiURL, childBody, signal)
      : Promise.resolve<RolodexLoadedEntryJson[]>([]),
  ])

  return flattenLoadedEntries([...primary, ...child])
}

function buildRowRecord(
  entry: GeckoEntry,
  pool: ReadonlyArray<GeckoEntry>
): PaginatedRolodexTableRecordType {
  const linkedTo = pool.filter(
    (linked) =>
      !!entry.tags[linked.type] && linked.id === entry.tags[linked.type]
  )
  const linkedFrom = pool.filter(
    (linked) => linked.tags[entry.type] === entry.id
  )
  return {
    id: entry.id,
    entries: [entry, ...linkedTo, ...linkedFrom],
  }
}

function renderRowCells(
  rec: PaginatedRolodexTableRecordType,
  columns: ReadonlyArray<ExplorerTableExportColumn>,
  schemaFields: OntologyContextType['fields']
): ReadonlyArray<string> {
  return columns.map((col) => {
    if (col.kind === 'data') {
      return extractColumnExportString(rec.entries, col.columnDef, schemaFields)
    }
    return col.column.exportValue?.(rec) ?? ''
  })
}

/**
 * Turn one batch's loaded entry pool into the spreadsheet rows for that
 * batch. Output ordering preserves the original group/base ID ordering
 * from the histogram so the export reflects the underlying drilldown
 * selection order; the table's local sort is intentionally ignored here.
 */
function renderBatchRows({
  batchGroupIds,
  baseIds,
  pool,
  groupIdToBaseId,
  groupByType,
  expandRecords,
  columns,
  schemaFields,
}: {
  batchGroupIds: ReadonlyArray<string>
  baseIds: ReadonlyArray<string>
  pool: ReadonlyArray<GeckoEntry>
  groupIdToBaseId: ReadonlyMap<string, ReadonlyArray<string>>
  groupByType: OntologyRolodexEntryType
  expandRecords?: (
    rows: ReadonlyArray<PaginatedRolodexTableRecordType>
  ) => ReadonlyArray<PaginatedRolodexTableRecordType>
  columns: ReadonlyArray<ExplorerTableExportColumn>
  schemaFields: OntologyContextType['fields']
}): ReadonlyArray<ReadonlyArray<string>> {
  const baseIdSet = new Set(baseIds)
  const rowEntriesById = new Map<string, GeckoEntry>()
  pool
    .filter((e) => e.type === groupByType && baseIdSet.has(e.id))
    .forEach((e) => rowEntriesById.set(e.id, e))

  const rowRecords: PaginatedRolodexTableRecordType[] = []
  batchGroupIds.forEach((gid) => {
    const baseIdsForGroup = groupIdToBaseId.get(gid) ?? []
    baseIdsForGroup.forEach((bid) => {
      const rowEntry = rowEntriesById.get(bid)
      if (!rowEntry) return
      rowRecords.push(buildRowRecord(rowEntry, pool))
    })
  })
  const recordsToExport = expandRecords ? expandRecords(rowRecords) : rowRecords
  return recordsToExport.map((rec) => [
    ...renderRowCells(rec, columns, schemaFields),
  ])
}

function escapeCsvCell(value: string): string {
  if (CSV_ESCAPE_PATTERN.test(value)) {
    return `${CSV_QUOTE}${value.replace(
      CSV_QUOTE_PATTERN,
      CSV_QUOTE_ESCAPE
    )}${CSV_QUOTE}`
  }
  return value
}

function rowsToCsv(rows: ReadonlyArray<ReadonlyArray<string>>): string {
  return rows
    .map((r) => r.map(escapeCsvCell).join(CSV_DELIMITER))
    .join(CSV_ROW_SEPARATOR)
}

async function buildXlsxBlob(
  header: ReadonlyArray<string>,
  rows: ReadonlyArray<ReadonlyArray<string>>
): Promise<Blob> {
  const workbook = new ExcelJS.Workbook()
  const sheet = workbook.addWorksheet(XLSX_WORKSHEET_NAME)
  sheet.addRow([...header])
  sheet.getRow(HEADER_ROW_INDEX).font = { bold: true }
  rows.forEach((r) => sheet.addRow([...r]))
  sheet.columns.forEach((column) => {
    let maxLen = DEFAULT_COLUMN_WIDTH
    column.eachCell?.((cell) => {
      const len = cell.value ? String(cell.value).length : 0
      if (len > maxLen) maxLen = Math.min(MAX_COLUMN_WIDTH, len)
    })
    column.width = maxLen + COLUMN_WIDTH_PADDING
  })
  const buffer = await workbook.xlsx.writeBuffer()
  return new Blob([buffer], {
    type: XLSX_MIME_TYPE,
  })
}

/**
 * Yield to the event loop so a long export does not freeze the tab between
 * batches. Gives React a chance to flush progress updates too.
 */
function nextTick(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, EVENT_LOOP_YIELD_MS))
}

export async function runExplorerTableExport(
  input: ExplorerTableExportInput
): Promise<ExplorerTableExportResult> {
  const {
    axios,
    apiURL,
    groupByType,
    links,
    childLinks,
    orderedGroupIds,
    groupIdToBaseId,
    expandRecords,
    columns,
    schemaFields,
    format,
    filename,
    truncated,
    batchSize = DEFAULT_BATCH_SIZE,
    maxRows = DEFAULT_MAX_ROWS,
    onProgress,
    signal,
  } = input

  const cappedGroupIds =
    orderedGroupIds.length > maxRows
      ? orderedGroupIds.slice(0, maxRows)
      : orderedGroupIds
  const truncatedByMaxRows = orderedGroupIds.length > cappedGroupIds.length

  const groupIdBatches: ReadonlyArray<ReadonlyArray<string>> = chunk(
    [...cappedGroupIds],
    batchSize
  )
  const headerRow = columns.map((c) => c.title)
  const rows: string[][] = []

  for (const batchGroupIds of groupIdBatches) {
    if (signal?.aborted) {
      throw new DOMException(EXPORT_CANCELLED_MESSAGE, ABORT_ERROR_NAME)
    }
    const baseIds = flatMap(batchGroupIds, (gid) => [
      ...(groupIdToBaseId.get(gid) ?? []),
    ])
    if (!baseIds.length) continue

    const pool = await loadBatch({
      axios,
      apiURL,
      groupByType,
      baseIds,
      links,
      childLinks,
      signal,
    })

    const batchRows = renderBatchRows({
      batchGroupIds,
      baseIds,
      pool,
      groupIdToBaseId,
      groupByType,
      expandRecords,
      columns,
      schemaFields,
    })
    batchRows.forEach((row) => rows.push([...row]))

    onProgress?.(rows.length, cappedGroupIds.length)
    await nextTick()
  }

  const blob =
    format === 'xlsx'
      ? await buildXlsxBlob(headerRow, rows)
      : new Blob([rowsToCsv([headerRow, ...rows])], {
          type: CSV_MIME_TYPE,
        })

  const ext = FILE_EXTENSION_BY_FORMAT[format]
  downloadBlob(blob, filename.endsWith(ext) ? filename : `${filename}${ext}`)

  return {
    rowsExported: rows.length,
    totalRows: orderedGroupIds.length,
    truncatedByHistogram: truncated,
    truncatedByMaxRows,
  }
}

/** Internal helpers exposed only for unit tests. */
export const __testing = {
  escapeCsvCell,
  rowsToCsv,
}
